import { BondEntry, EquityEntry, HoldingsReport } from '@ndvr/reporting-api';

import { formatDate } from 'utils';
import { parseNumberStr } from 'utils/number-utils';

import type { AssetAllocationSegmentKey } from './asset-allocation-presentation';

// The per-asset-class entry lists whose lengths make up the consolidated position count.
// Cash, accruals and futures/options exposure are balances, not counted positions.
const POSITION_ENTRY_LISTS = ['equities', 'bonds', 'leveredBonds', 'mutualFunds', 'options', 'others'] as const;

export interface HoldingsSummary {
    // Consolidated market value across every asset class and cash balance.
    marketValue: number;
    // Total unrealized gain/loss across positions that carry a cost basis.
    unrealized: number;
    // Implied cost basis (market value less unrealized gain/loss).
    costBasis: number;
    // Number of distinct security positions (excludes cash balances).
    positions: number;
}

export interface AllocationSegment {
    key: AssetAllocationSegmentKey;
    label: string;
    amount: number;
}

export interface AssetAllocation {
    total: number;
    // Highest-level buckets, in display order; zero-value buckets are dropped so the
    // bar and legend only show classes the household actually holds.
    segments: AllocationSegment[];
}

// Advisor-facing explanations of how each summary figure is derived. Kept in this
// file, next to the calculations, so the copy is updated together with the math it
// describes — if a formula below changes, change its tooltip here too.
export const HOLDINGS_SUMMARY_TOOLTIPS = {
    marketValue:
        'Sum of the market values of equities, mutual funds, bonds, bond exposure, options and other holdings, plus cash & equivalents.',
    unrealized:
        'Sum of the unrealized gain / loss across all security positions. Cash & equivalents carry no unrealized gain / loss.',
    costBasis: 'Total market value less unrealized gain / loss.',
} as const;

// Sum of every asset-class market value plus the cash bucket — the report has no
// single "total market value" field, so it is derived from the underlying buckets,
// mirroring how the client-portal holdings rollup treats each class.
//
// `totalCash` is the backend's cash-and-equivalents aggregate (it already includes
// cash, the cash-equivalent ETF, and dividend/broker-interest accruals), so those
// sub-components must NOT be added again here.
const marketValueOf = (report: HoldingsReport): number =>
    parseNumberStr(report.totalEquityPerformance?.value) +
    parseNumberStr(report.totalMutualFundPerformance?.value) +
    parseNumberStr(report.totalBondPerformance?.value) +
    parseNumberStr(report.totalLeveredBondPerformance?.value) +
    parseNumberStr(report.totalOptionsPerformance?.value) +
    parseNumberStr(report.totalOtherPerformance?.value) +
    parseNumberStr(report.totalCash);

export const getHoldingsSummary = (report: HoldingsReport): HoldingsSummary => {
    const marketValue = marketValueOf(report);
    const unrealized =
        parseNumberStr(report.totalEquityPerformance?.unrealizedReturn) +
        parseNumberStr(report.totalMutualFundPerformance?.unrealizedReturn) +
        parseNumberStr(report.totalBondPerformance?.unrealizedReturn) +
        parseNumberStr(report.totalLeveredBondPerformance?.unrealizedReturn) +
        parseNumberStr(report.totalOptionsPerformance?.unrealizedReturn) +
        parseNumberStr(report.totalOtherPerformance?.unrealizedReturn);

    const positions = POSITION_ENTRY_LISTS.reduce((sum, key) => sum + (report[key]?.length ?? 0), 0);

    return { marketValue, unrealized, costBasis: marketValue - unrealized, positions };
};

// How each allocation bucket below is composed; shown as a tooltip on the legend.
// Must stay in sync with the bucket sums in getAssetAllocation.
export const ALLOCATION_SEGMENT_TOOLTIPS: Record<AllocationSegment['key'], string> = {
    equities: 'Equity positions plus equity mutual funds.',
    bonds: 'Bond positions plus levered bond exposure.',
    cash: 'Cash, cash-equivalent ETFs, and dividend & broker-interest accruals.',
    other: 'Options and other instruments.',
};

// Buckets the report's asset-class values into the highest-level allocation groups.
// Cash uses `totalCash` (the backend cash-and-equivalents aggregate) directly, so the
// segment total equals the hero's market value and the two views always reconcile.
export const getAssetAllocation = (report: HoldingsReport): AssetAllocation => {
    const equities =
        parseNumberStr(report.totalEquityPerformance?.value) + parseNumberStr(report.totalMutualFundPerformance?.value);
    const bonds =
        parseNumberStr(report.totalBondPerformance?.value) + parseNumberStr(report.totalLeveredBondPerformance?.value);
    const cash = parseNumberStr(report.totalCash);
    const other =
        parseNumberStr(report.totalOptionsPerformance?.value) + parseNumberStr(report.totalOtherPerformance?.value);

    const allSegments: AllocationSegment[] = [
        { key: 'equities', label: 'Equities', amount: equities },
        { key: 'bonds', label: 'Bonds', amount: bonds },
        { key: 'cash', label: 'Cash & Equivalents', amount: cash },
        { key: 'other', label: 'Other', amount: other },
    ];
    const segments = allSegments.filter((segment) => segment.amount > 0);
    const total = segments.reduce((sum, segment) => sum + segment.amount, 0);

    return { total, segments };
};

// One display row of the holdings breakdown table, shared by all three tabs. Cash rows
// carry null price/quantity (rendered as '-') and fixed zero gain/return, since cash
// balances have no market price or cost basis.
export interface HoldingRow {
    // '' when the instrument has no ticker (plain bonds, cash rows).
    symbol: string;
    name: string;
    // Second line under the security name (bonds: formatted maturity date).
    secondaryText?: string;
    assetClass: string;
    // Percent of the scope's market value (the API weight is a fraction, converted here).
    weight: number;
    price: number | null;
    quantity: number | null;
    marketValue: number;
    unrealizedGainLoss: number;
    returnPercent: number;
}

// Maps a security entry to a display row. Equities, mutual funds and levered bonds share
// the EquityEntry shape; bonds differ only in having a maturity date instead of a ticker
// (the maturity date becomes the second line under the name, matching the client-portal
// bond table). Two conversions to know about:
// - The API weight is a fraction of the scope total (0.1517 = 15.17%); converted to
//   percent once here, the same convention the client-portal holdings tables use.
// - HoldingPerformance field names are misleading: `value` is the market value,
//   `unrealizedReturn` is the unrealized PnL *amount* (the backend maps unrealizedPnl
//   into it), and `unrealizedReturnPercent` is the return in percent, already
//   multiplied by 100 by the backend.
const toRow = (entry: EquityEntry | BondEntry, assetClass: string): HoldingRow => ({
    symbol: 'ticker' in entry ? (entry.ticker ?? '') : '',
    name: entry.displayName,
    secondaryText: 'maturityDate' in entry ? formatDate(entry.maturityDate, 'HUMAN') || undefined : undefined,
    assetClass,
    weight: parseNumberStr(entry.weight) * 100,
    price: parseNumberStr(entry.price),
    quantity: parseNumberStr(entry.quantity),
    marketValue: parseNumberStr(entry.performance?.value),
    unrealizedGainLoss: parseNumberStr(entry.performance?.unrealizedReturn),
    returnPercent: parseNumberStr(entry.performance?.unrealizedReturnPercent),
});

const byMarketValueDesc = (a: HoldingRow, b: HoldingRow) => b.marketValue - a.marketValue;

// Equities tab = equities + equity mutual funds, matching the allocation's "Equities"
// bucket so the tab total reconciles with getAssetAllocation. The asset-class label is
// derived from the source list — the API carries no per-entry asset type (see the plan's
// "Missing data" note for the richer labels).
export const getEquityRows = (report: HoldingsReport): HoldingRow[] =>
    [
        ...(report.equities ?? []).map((entry) => toRow(entry, 'Equity')),
        ...(report.mutualFunds ?? []).map((entry) => toRow(entry, 'Mutual Fund')),
    ].sort(byMarketValueDesc);

// Bonds tab = bonds + levered bond exposure (EquityEntry-shaped, held via tickers),
// matching the allocation's "Bonds" bucket.
export const getBondRows = (report: HoldingsReport): HoldingRow[] =>
    [
        ...(report.bonds ?? []).map((entry) => toRow(entry, 'Bond')),
        ...(report.leveredBonds ?? []).map((entry) => toRow(entry, 'Levered Bond')),
    ].sort(byMarketValueDesc);

// Cash & Equivalents tab: the four fixed balances the report exposes as scalars,
// mirroring the client-portal Cash view. Balances have no price, quantity, gain/loss or
// return — the table renders '-', $0.00 and 0.00% for them per design. Zero balances
// are dropped, matching getAssetAllocation dropping zero-value buckets, so a household
// without cash gets the tab's empty state instead of four $0.00 rows.
export const getCashRows = (report: HoldingsReport): HoldingRow[] => {
    const cashRow = (name: string, value?: string, weight?: string): HoldingRow => ({
        symbol: '',
        name,
        assetClass: 'Cash & Equivalents',
        // Same fraction-to-percent conversion as toRow.
        weight: parseNumberStr(weight) * 100,
        price: null,
        quantity: null,
        marketValue: parseNumberStr(value),
        unrealizedGainLoss: 0,
        returnPercent: 0,
    });

    return [
        cashRow('Cash', report.cash, report.cashWeight),
        cashRow('Dividend Accruals', report.dividendAccruals, report.dividendAccrualsWeight),
        cashRow('Broker Interest Accruals', report.brokerInterestAccruals, report.brokerInterestAccrualsWeight),
        cashRow('Cash Equivalent ETF', report.cashEquivalentETF, report.cashEquivalentETFWeight),
    ].filter((row) => row.marketValue !== 0);
};

export interface HoldingsTotal {
    weight: number;
    marketValue: number;
    unrealizedGainLoss: number;
    returnPercent: number;
}

// Total row of one holdings tab. Weight, market value and gain/loss are plain sums of the
// rows. The return is the total gain/loss divided by the total cost basis (market value less
// gain/loss), so a large position moves it more than a small one — averaging the row returns
// would not. With no cost basis (e.g. only cash balances) the return is 0%, like a cash row's.
export const getHoldingsTotal = (rows: HoldingRow[]): HoldingsTotal => {
    const sum = (pick: (row: HoldingRow) => number) => rows.reduce((total, row) => total + pick(row), 0);
    const marketValue = sum((row) => row.marketValue);
    const unrealizedGainLoss = sum((row) => row.unrealizedGainLoss);
    const costBasis = marketValue - unrealizedGainLoss;

    return {
        weight: sum((row) => row.weight),
        marketValue,
        unrealizedGainLoss,
        returnPercent: costBasis === 0 ? 0 : (unrealizedGainLoss / costBasis) * 100,
    };
};
